import React from 'react';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { formatDollar, formatOptionalDate } from '../utils/format';
import { apiAbsoluteUrl } from '../utils/pdf';
import { useInvoicePdfUpload } from '../hooks/useInvoicePdfUpload';

interface Props {
  documents: CollectionInvoiceDocument[];
  location: string;
  acctNo: string;
  onUploaded: (applyTo: string, pdf: CollectionInvoicePdf) => void;
}

const ClientInvoicesTable: React.FC<Props> = ({ documents, location, acctNo, onUploaded }) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded,
  });

  return (
    <Card>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      {documents.length === 0 ? (
        <Box sx={{ p: 3, color: 'text.secondary' }}>
          <Typography variant="body2">No past-due invoices.</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Invoice Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>PDF</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.apply_to} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{doc.apply_to}</TableCell>
                  <TableCell>{formatOptionalDate(doc.invoice_date)}</TableCell>
                  <TableCell>{formatOptionalDate(doc.due_date)}</TableCell>
                  <TableCell align="right">{formatDollar(doc.amount)}</TableCell>
                  <TableCell>
                    {doc.pdf ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {doc.pdf.filename}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary">
                          No PDF available
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<UploadFileIcon />}
                          disabled={!canUpload || uploadingApplyTo === doc.apply_to}
                          onClick={() => triggerUpload(doc.apply_to)}
                        >
                          {uploadingApplyTo === doc.apply_to ? 'Uploading…' : 'Upload PDF'}
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {doc.pdf?.download_url ? (
                      <Button
                        component="a"
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        href={apiAbsoluteUrl(doc.pdf.download_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={doc.pdf.filename}
                      >
                        Download
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

export default ClientInvoicesTable;
